import { IconName } from '@fortawesome/fontawesome-svg-core';
import { EWorldMapSetType } from '__generated__/globalTypes';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { SelectValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectValue';
import { TextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { WorldMap_worldMap_worldMapSets } from 'graphql/query/__generated__/WorldMap';
import { useMapheimDebounced } from 'hooks/general/useMapheimDebounced';
import { useMapSetActions } from 'hooks/map/useMapSetActions';
import { sortBy } from 'lodash';
import { useContext } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal } from 'session/ctxModal';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    mapSetType: EWorldMapSetType;
    sectionIcon: IconName;
    allVisibleOnLoad?: boolean;
    strings: {
        sectionTitle: string;
        addButtonTitle: string;
        deleteConfirmationMessage: string;
        newSetTitle: string;
    };
}

export const GeneralMapSetSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const mapSetActions = useMapSetActions();
    const ctxModal = useContext(CtxModal);

    const mapSets = sortBy(
        ctxEdit.map?.worldMapSets?.filter((e) => e.type === props.mapSetType) ?? [],
        (e) => e.orderIndex
    );

    function updateMapSet(data: {
        update: Partial<WorldMap_worldMap_worldMapSets>;
        fullMapSet: WorldMap_worldMap_worldMapSets;
    }) {
        mapSetActions.updateSet(data.fullMapSet.worldMapSetId, data.update, data.fullMapSet);
    }

    function updateMapSetOrder(
        set1: WorldMap_worldMap_worldMapSets,
        set2: WorldMap_worldMap_worldMapSets
    ) {
        // The random number fixes it when no orderIndex exists
        const random = Math.random();

        mapSetActions.updateSets([
            {
                update: { orderIndex: set2.orderIndex || random - 1 },
                originalData: set1
            },
            {
                update: { orderIndex: set1.orderIndex || random + 1 },
                originalData: set2
            }
        ]);
    }

    const updateMapSetDebounced = useMapheimDebounced(updateMapSet, 200);

    function deleteMapSet(worldMapSetId: string) {
        ctxModal.setModal({
            type: EModal.CONFIRM,
            data: {
                text: props.strings.deleteConfirmationMessage,
                onConfirm: () => {
                    mapSetActions.removeSet(worldMapSetId);
                }
            } as IConfirmActionModal
        });
    }

    return (
        <S_MapSettingsSection>
            <SidebarSection
                icon={{ iconName: props.sectionIcon }}
                title={props.strings.sectionTitle}
            >
                <SidebarContent>
                    <S_BorderedRows>
                        {mapSets.map((mapSet, i) => (
                            <S_Row key={mapSet.worldMapSetId}>
                                <S_TitleName
                                    value={mapSet.title}
                                    onChange={(value) => {
                                        updateMapSetDebounced({
                                            fullMapSet: mapSet,
                                            update: { title: value }
                                        });
                                    }}
                                />
                                <S_RowRight>
                                    <S_Arrows $alignBottom={i === 0}>
                                        {i !== 0 && (
                                            <S_ActionButtonIcon
                                                icon={{ iconName: 'arrow-up', iconSize: 10 }}
                                                onClick={() => {
                                                    const previousMapSet = mapSets[i - 1];

                                                    updateMapSetOrder(previousMapSet, mapSet);
                                                }}
                                            />
                                        )}
                                        {i !== mapSets.length - 1 && (
                                            <S_ActionButtonIcon
                                                icon={{ iconName: 'arrow-down', iconSize: 10 }}
                                                onClick={() => {
                                                    const nextMapSet = mapSets[i + 1];

                                                    updateMapSetOrder(nextMapSet, mapSet);
                                                }}
                                            />
                                        )}
                                    </S_Arrows>
                                    <ActionButton
                                        icon={{ iconName: 'trash-can' }}
                                        onClick={() => {
                                            deleteMapSet(mapSet.worldMapSetId);
                                        }}
                                    />
                                </S_RowRight>
                            </S_Row>
                        ))}
                    </S_BorderedRows>
                    <S_ActionButton
                        text={props.strings.addButtonTitle}
                        icon={{ iconName: 'plus' }}
                        onClick={() => {
                            const newMapSet =
                                Math.floor(Math.max(...mapSets.map((e) => e.orderIndex), 0)) + 1;

                            const isVisibleOnLoad = props.allVisibleOnLoad || mapSets.length === 0;

                            mapSetActions.createSet({
                                title: `${props.strings.newSetTitle} ${newMapSet}`,
                                type: props.mapSetType,
                                orderIndex: newMapSet,
                                imageUrl: null,
                                color: null,
                                isVisibleOnLoad
                            });
                        }}
                    />
                </SidebarContent>
                {mapSets.length > 0 && !props.allVisibleOnLoad && (
                    <SidebarSubsection
                        title={Localization.VISIBLE_ON_LOAD}
                        icon={{ iconName: 'eye' }}
                    >
                        <SidebarContent>
                            <SelectValue
                                options={mapSets.map((mapSet) => ({
                                    label: mapSet.title,
                                    value: mapSet.worldMapSetId
                                }))}
                                selectedOption={(() => {
                                    const mapSet = mapSets.find((mapSet) => mapSet.isVisibleOnLoad);
                                    return mapSet
                                        ? {
                                              label: mapSet.title,
                                              value: mapSet.worldMapSetId
                                          }
                                        : null;
                                })()}
                                onChange={({ value }) => {
                                    const newVisibleMapSet = mapSets.find(
                                        (e) => e.worldMapSetId === value
                                    );
                                    const mapSetsToUpdate = mapSets
                                        .filter((e) => e.isVisibleOnLoad)
                                        .filter(
                                            (e) =>
                                                e.worldMapSetId !== newVisibleMapSet.worldMapSetId
                                        );

                                    mapSetActions.updateSets([
                                        {
                                            update: { isVisibleOnLoad: true },
                                            originalData: newVisibleMapSet
                                        },
                                        ...mapSetsToUpdate.map((mapSet) => ({
                                            update: { isVisibleOnLoad: false },
                                            originalData: mapSet
                                        }))
                                    ]);
                                }}
                            />
                        </SidebarContent>
                    </SidebarSubsection>
                )}
            </SidebarSection>
        </S_MapSettingsSection>
    );
};

const S_MapSettingsSection = styled.div``;

const S_BorderedRows = styled.div`
    margin-bottom: 10px;
`;

const S_ActionButton = styled(ActionButton)`
    width: fit-content;
`;

const S_Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    color: #141c54;
    border-bottom: 1px solid #dadada;
    padding: 11px 0;
    margin-bottom: 5px;
`;

const S_RowRight = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const S_Arrows = styled.div<{ $alignBottom: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: ${(props) => (props.$alignBottom ? 'end' : 'space-between')};
`;

const S_TitleName = styled(TextValue)`
    text-align: left;
    width: 150px;
`;

const S_ActionButtonIcon = styled(ActionButton)`
    width: 16px;
    height: 16px;
    min-height: 16px;
    border-radius: 3px;
`;
